import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import PageLayout from "../components/layout/PageLayout";
import InlineBanner from "../components/ui/InlineBanner";
import HostOverviewCard, { getHostDisplayName, sumEarnings } from "../components/hostComponents/HostOverviewCard";
import HostNotesCard from "../components/hostComponents/HostNotesCard";
import EntryInstructionsEmailSettings from "../components/hostComponents/EntryInstructionsEmailSettings";
import HostPmsIntegrationStatus, { getConnectedProviders } from "../components/hostComponents/HostPmsIntegrationStatus";
import HostPropertiesTable from "../components/hostComponents/HostPropertiesTable";
import {
  deletePropertyReset,
  updatePropertyReset,
} from "../store/slices/propertySlices";
import {
  getHostDetails,
  getHostEarnings,
  getHostPendingEarnings,
  getHostProperties,
} from "../store/thunks/HostActions";

const NOTICE_TIMEOUT_MS = 3000;

function HostDetailScreen() {
  const dispatch = useDispatch();
  const { hid } = useParams();

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const { host, loading: loadingHost, error: errorDetails } = useSelector((state) => state.hostDetails);
  const { properties, loading: loadingProperties } = useSelector((state) => state.hostProperties);
  const { earnings: hostEarnings, loading: loadingEarnings } = useSelector((state) => state.hostEarnings);
  const { earnings: hostPendingEarnings } = useSelector((state) => state.hostPendingEarnings);
  const {
    success: successUpdate,
    message: messageUpdate,
    error: errorUpdate,
  } = useSelector((state) => state.propertyUpdate);
  const {
    success: successDelete,
    message: messageDelete,
    error: errorDelete,
  } = useSelector((state) => state.propertyDelete);

  const loadHost = useCallback(async ({ silent = false } = {}) => {
    if (!hid) return;
    if (silent) setRefreshing(true);
    try {
      await Promise.all([
        dispatch(getHostDetails(hid)),
        dispatch(getHostProperties(hid)),
        dispatch(getHostEarnings(hid)),
        dispatch(getHostPendingEarnings(hid)),
      ]);
    } finally {
      if (silent) setRefreshing(false);
    }
  }, [dispatch, hid]);

  useEffect(() => {
    loadHost();
  }, [loadHost]);

  // Surface property update/delete results from the shared slices, then
  // reset them so the banner does not reappear on the next visit.
  useEffect(() => {
    if (errorUpdate) setErrorMessage(errorUpdate);
    if (errorDelete) setErrorMessage(errorDelete);
    if (successUpdate) setSuccessMessage(messageUpdate || "Property updated.");
    if (successDelete) setSuccessMessage(messageDelete || "Property deleted.");
    if (successUpdate || successDelete) dispatch(getHostProperties(hid));

    if (!(successUpdate || successDelete || errorUpdate || errorDelete)) return undefined;
    const timeoutId = setTimeout(() => {
      if (successUpdate || errorUpdate) dispatch(updatePropertyReset());
      if (successDelete || errorDelete) dispatch(deletePropertyReset());
    }, NOTICE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [dispatch, errorDelete, errorUpdate, hid, messageDelete, messageUpdate, successDelete, successUpdate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return undefined;
    const timeoutId = setTimeout(() => {
      setSuccessMessage(null);
      setErrorMessage(null);
    }, NOTICE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [successMessage, errorMessage]);

  const activeProperties = useMemo(
    () => (Array.isArray(properties) ? properties.filter((property) => !property?.isDeleted) : []),
    [properties]
  );
  const connectedPmsCount = useMemo(() => getConnectedProviders(host).length, [host]);
  const completedEarnings = useMemo(() => sumEarnings(hostEarnings), [hostEarnings]);
  const pendingEarnings = useMemo(() => sumEarnings(hostPendingEarnings), [hostPendingEarnings]);

  const hostName = host ? getHostDisplayName(host) : "Host";

  return (
    <PageLayout
      title={hostName}
      subtitle={host?.companyName && host.companyName !== hostName ? host.companyName : "Property manager"}
      breadcrumbs={[{ label: "Property Managers", path: "/hostList" }, { label: hostName }]}
      showSearch={false}
      fullWidth
      actions={(
        <button
          type="button"
          onClick={() => loadHost({ silent: true })}
          disabled={loadingHost || refreshing}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      )}
    >
      {errorDetails ? <InlineBanner tone="error">{errorDetails}</InlineBanner> : null}
      {errorMessage ? (
        <InlineBanner tone="error" onClose={() => setErrorMessage(null)}>{errorMessage}</InlineBanner>
      ) : null}
      {successMessage ? (
        <InlineBanner tone="success" onClose={() => setSuccessMessage(null)}>{successMessage}</InlineBanner>
      ) : null}

      <div className="space-y-3">
        <HostOverviewCard
          host={host}
          loading={loadingHost}
          propertyCount={activeProperties.length}
          connectedPmsCount={connectedPmsCount}
          completedEarnings={completedEarnings}
          pendingEarnings={pendingEarnings}
          earningsLoading={loadingEarnings}
        />

        <div className="grid grid-cols-1 gap-3 laptop:grid-cols-12">
          <div className="space-y-3 laptop:col-span-7">
            <HostNotesCard
              host={host}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
            />
            <EntryInstructionsEmailSettings
              host={host}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
            />
          </div>
          <div className="laptop:col-span-5">
            <HostPmsIntegrationStatus
              host={host}
              properties={properties}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
            />
          </div>
        </div>

        <HostPropertiesTable data={activeProperties} loading={loadingProperties} />
      </div>
    </PageLayout>
  );
}

export default HostDetailScreen;
